import React from "react";
import {
  Card,
  Typography,
  Chip,
  Switch,
  IconButton,
  Tooltip,
  Collapse,
} from "@/components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faPlay } from "@fortawesome/free-solid-svg-icons";
import { getSourceFromPath } from "@/utils/tracker-helpers";
import { SourceIcon } from "@/components/resource-card";
import { TrackerViewProps } from "../types";
import { useTrackerActions } from "../hooks/useTrackerActions";
import TrackerTagManagerInline from "../components/tracker-tag-manager-inline";
import GroupHeader from "@/components/group-header";
import { Tracker } from "@/models/tracker";
import ResourceGroupCard from "@/components/resource-group-card";
import { ResourceGroup, getGroupRefId } from "@/models/resource-group";
import { trackerGalleryConfig } from "@/config/resource-gallery-configs";
import type { ViewSize } from "@/components/layout/gallery-layout/types";

const LIST_GAP: Record<ViewSize, string> = {
  small: "space-y-0.5",
  medium: "space-y-2",
  large: "space-y-3",
};

const TrackerListView: React.FC<TrackerViewProps> = ({
  trackers,
  onToggleActive,
  onOpenExecutions,
  getTrackerValue,
  trackersTagsMap,
  onTagsUpdate,
  groupBy = "none",
  groupedTrackers,
  collapsedGroups = new Set(),
  onToggleGroup,
  resourceGroups,
  expandedFolders = new Set(),
  onToggleFolder,
  viewSize = "medium",
}) => {
  const { runTracker, isRunning } = useTrackerActions();

  const handleRunTracker = async (trackerId: string) => {
    await runTracker(trackerId);
  };

  const renderTrackerCard = (tracker: Tracker) => {
    const trackerValue = getTrackerValue(tracker);
    const source = getSourceFromPath(tracker);

    if (viewSize === "small") {
      return (
        <Card
          key={tracker.id}
          className="px-2.5 py-1 rounded-arc-sm transition-all hover:shadow-arc-md shadow-arc-sm"
        >
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <SourceIcon source={source} size="sm" />
            </div>
            <span className="text-xs font-medium text-content-primary truncate flex-1 min-w-0">
              {trackerValue?.value || tracker.id}
            </span>
            {!tracker.is_import && (
              <Switch
                checked={tracker.status === "active"}
                onChange={(checked) => onToggleActive(tracker.id, checked)}
                size="sm"
              />
            )}
            <div className="flex items-center flex-shrink-0">
              {!tracker.is_import && (
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRunTracker(tracker.id)}
                  disabled={isRunning}
                  className="text-success hover:bg-success-light/20"
                >
                  <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
                </IconButton>
              )}
              <IconButton
                size="sm"
                variant="ghost"
                onClick={() => onOpenExecutions(tracker)}
              >
                <FontAwesomeIcon icon={faList} className="text-[10px]" />
              </IconButton>
            </div>
          </div>
        </Card>
      );
    }

    if (viewSize === "large") {
      return (
        <Card
          key={tracker.id}
          className="p-5 rounded-arc-lg transition-all hover:shadow-arc-md shadow-arc-sm"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-surface-secondary">
              <SourceIcon source={source} size="md" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <Typography variant="h5" className="font-semibold text-content-primary">
                  {trackerValue?.value || tracker.id}
                </Typography>
                <Chip
                  label={tracker.is_import ? "Import" : "Monitoring"}
                  size="sm"
                  variant="outlined"
                />
              </div>
              <Typography variant="text-base" className="text-content-secondary mb-3">
                {source || "N/A"}
              </Typography>
              <TrackerTagManagerInline
                tracker={tracker}
                trackersTagsMap={trackersTagsMap}
                onTagsUpdate={onTagsUpdate}
              />
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {!tracker.is_import && (
                <Switch
                  checked={tracker.status === "active"}
                  onChange={(checked) => onToggleActive(tracker.id, checked)}
                  size="sm"
                />
              )}
              {!tracker.is_import && (
                <Tooltip content="Exécuter le tracker">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRunTracker(tracker.id)}
                    disabled={isRunning}
                    className="text-success hover:bg-success-light/20"
                  >
                    <FontAwesomeIcon icon={faPlay} className="text-sm" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip content="Voir les exécutions">
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpenExecutions(tracker)}
                >
                  <FontAwesomeIcon icon={faList} className="text-sm" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </Card>
      );
    }

    return (
      <Card
        key={tracker.id}
        className="px-3 py-2.5 rounded-arc-md transition-all hover:shadow-arc-md shadow-arc-sm"
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <SourceIcon source={source} size="sm" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Typography variant="text-sm" className="font-semibold text-content-primary truncate">
                {trackerValue?.value || tracker.id}
              </Typography>
              <Chip
                label={tracker.is_import ? "Import" : "Monitoring"}
                size="sm"
                variant="outlined"
              />
            </div>
            <div className="flex items-center gap-3">
              <Typography variant="text-xs" className="text-content-secondary">
                {source || "N/A"}
              </Typography>
              <TrackerTagManagerInline
                tracker={tracker}
                trackersTagsMap={trackersTagsMap}
                onTagsUpdate={onTagsUpdate}
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!tracker.is_import && (
              <Switch
                checked={tracker.status === "active"}
                onChange={(checked) => onToggleActive(tracker.id, checked)}
                size="sm"
              />
            )}
            {!tracker.is_import && (
              <Tooltip content="Exécuter le tracker">
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRunTracker(tracker.id)}
                  disabled={isRunning}
                  className="text-success hover:bg-success-light/20"
                >
                  <FontAwesomeIcon icon={faPlay} className="text-xs" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip content="Voir les exécutions">
              <IconButton
                size="sm"
                variant="ghost"
                onClick={() => onOpenExecutions(tracker)}
              >
                <FontAwesomeIcon icon={faList} className="text-xs" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </Card>
    );
  };

  const renderList = (trackersToRender: Tracker[]) => (
    <div className={LIST_GAP[viewSize]}>
      {trackersToRender.map((tracker) => renderTrackerCard(tracker))}
    </div>
  );

  // Always show folders when they exist
  if (resourceGroups && onToggleFolder && expandedFolders) {
    const addFoldersRecursively = (
      items: Array<{ type: "folder"; data: any; level: number }>,
      parentId: string | null,
      level: number = 0,
    ) => {
      const foldersAtLevel = resourceGroups.filter((folder) => {
        const folderParentId = folder.parent?.id || null;
        return folderParentId === parentId;
      });

      foldersAtLevel.forEach((folder) => {
        items.push({ type: "folder", data: folder, level });
        if (expandedFolders.has(folder.id)) {
          addFoldersRecursively(items, folder.id, level + 1);
        }
      });
    };

    // No grouping: show folders + ungrouped trackers as flat list
    if (groupBy === "none") {
      const combinedItems: Array<{
        type: "folder" | "tracker";
        data: any;
        level: number;
      }> = [];

      addFoldersRecursively(combinedItems as any, null, 0);

      trackers.forEach((tracker) => {
        if (!tracker.group) {
          combinedItems.push({ type: "tracker", data: tracker, level: 0 });
        }
      });

      return (
        <div className={LIST_GAP[viewSize]}>
          {combinedItems.map((item) => {
            if (item.type === "folder") {
              const folder = item.data;
              const trackersInFolder = trackers.filter((t) => {
                const groupId = getGroupRefId(t.group);
                return groupId === folder.id;
              });
              const isExpanded = expandedFolders.has(folder.id);

              return (
                <ResourceGroupCard
                  key={`folder-${folder.id}`}
                  group={folder}
                  resourceCount={trackersInFolder.length}
                  isExpanded={isExpanded}
                  onToggle={() => onToggleFolder(folder.id)}
                  viewMode="list"
                  viewSize={viewSize}
                  level={item.level}
                >
                  {renderList(trackersInFolder)}
                </ResourceGroupCard>
              );
            } else {
              return renderTrackerCard(item.data);
            }
          })}
        </div>
      );
    }

    // Grouping active: only show grouped view
    if (groupedTrackers && onToggleGroup) {
      return (
        <div className="space-y-4">
          {Object.entries(groupedTrackers).map(
            ([groupKey, groupTrackers]) => {
              const { getGroupKey } = trackerGalleryConfig;

              const foldersInGroup = resourceGroups.filter((folder) => {
                const trackersInFolder = trackers.filter((t) => {
                  const groupId = getGroupRefId(t.group);
                  return groupId === folder.id;
                });
                return trackersInFolder.some((tracker) => {
                  const trackerGroupKey = getGroupKey(tracker, groupBy);
                  return trackerGroupKey === groupKey;
                });
              });

              const ungroupedTrackersInGroup = groupTrackers.filter(
                (t) => !t.group,
              );

              const totalCount =
                foldersInGroup.length + ungroupedTrackersInGroup.length;

              if (totalCount === 0) return null;

              const isCollapsed = collapsedGroups.has(groupKey);

              return (
                <div key={groupKey} className="flex flex-col gap-3">
                  <GroupHeader
                    groupKey={groupKey}
                    groupCount={totalCount}
                    groupBy={groupBy}
                    isCollapsed={isCollapsed}
                    onToggle={() => onToggleGroup(groupKey)}
                    resourceType="tracker"
                    emptyGroupLabel="Tous les trackers"
                  />
                  {!isCollapsed && (
                    <div className="pl-6">
                      <div className={LIST_GAP[viewSize]}>
                        {foldersInGroup.map((folder) => {
                          const trackersInFolder = trackers.filter((t) => {
                            const groupId = getGroupRefId(t.group);
                            return groupId === folder.id;
                          });
                          const trackersInFolderForGroup =
                            trackersInFolder.filter((tracker) => {
                              const trackerGroupKey = getGroupKey(
                                tracker,
                                groupBy,
                              );
                              return trackerGroupKey === groupKey;
                            });
                          const isExpanded = expandedFolders.has(folder.id);

                          return (
                            <ResourceGroupCard
                              key={`folder-${folder.id}-group-${groupKey}`}
                              group={folder}
                              resourceCount={
                                trackersInFolderForGroup.length
                              }
                              isExpanded={isExpanded}
                              onToggle={() => onToggleFolder(folder.id)}
                              viewMode="list"
                              viewSize={viewSize}
                            >
                              {renderList(trackersInFolderForGroup)}
                            </ResourceGroupCard>
                          );
                        })}

                        {renderList(ungroupedTrackersInGroup)}
                      </div>
                    </div>
                  )}
                </div>
              );
            },
          )}
        </div>
      );
    }
  }

  // Fallback: render plain list
  if (!groupedTrackers || !onToggleGroup || groupBy === "none") {
    return renderList(trackers);
  }

  // Regular grouping without folders
  return (
    <div className="flex flex-col gap-6">
      {Object.entries(groupedTrackers).map(([groupKey, groupTrackers]) => {
        const isCollapsed = collapsedGroups.has(groupKey);

        return (
          <div key={groupKey} className="flex flex-col gap-3">
            <GroupHeader
              groupKey={groupKey}
              groupCount={groupTrackers.length}
              groupBy={groupBy}
              isCollapsed={isCollapsed}
              onToggle={() => onToggleGroup(groupKey)}
              resourceType="tracker"
              emptyGroupLabel="Tous les trackers"
            />
            <Collapse in={!isCollapsed}>{renderList(groupTrackers)}</Collapse>
          </div>
        );
      })}
    </div>
  );
};

export default TrackerListView;
